import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/auth';
import { Link } from 'react-router-dom';
import Pagination from '../components/Pagination';

type TriggerType = 'all' | 'rebuild' | 'blend' | 'extension_use' | 'extension_create' | 'upscale' | 'custom' | 'unknown';
type ErrorStage = 'all' | 'ai_generation' | 'result_upload' | 'thumbnail_generation' | 'unknown';
/** A row is either a whole failed generation or one failed provider attempt
 *  inside a generation — which may itself have succeeded on a later attempt. */
type Outcome = 'all' | 'generation' | 'attempt' | 'recovered' | 'unrecovered';

const OUTCOME_QUERY: Record<Outcome, { scope: string; recovered: string }> = {
  all: { scope: 'all', recovered: 'all' },
  generation: { scope: 'generation', recovered: 'all' },
  attempt: { scope: 'attempt', recovered: 'all' },
  recovered: { scope: 'attempt', recovered: 'true' },
  unrecovered: { scope: 'attempt', recovered: 'false' },
};

/** What the attempt asked the provider for (prompt and images excluded). */
interface RequestParams {
  aspectRatio?: string;
  imageSize?: string;
  numberOfImages?: number;
  isBlendMode?: boolean;
  inputImageCount?: number;
  allowFallbackProvider?: boolean;
  extraParams?: Array<{ key: string; value: string | number | boolean }>;
}

interface FailedLog {
  _id: string;
  scope?: 'generation' | 'attempt';
  triggerType: string;
  userId?: string;
  userName?: string | null;
  extensionId?: string;
  generationId?: string;
  generationLogId?: string;
  errorMessage: string;
  errorStage: string;
  aiModel?: string;
  prompt?: string;
  attemptNumber?: number;
  provider?: string;
  errorCode?: string;
  errorCategory?: string;
  durationMs?: number;
  recovered?: boolean;
  providerModel?: string;
  requestParams?: RequestParams;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const TRIGGER_COLORS: Record<string, string> = {
  rebuild: 'var(--color-accent-strong)',
  blend: '#8957e5',
  extension_use: 'var(--color-warning)',
  extension_create: 'var(--color-success-strong)',
  upscale: '#1f9c8f',
  custom: '#d97706',
  unknown: 'var(--color-subtle)',
};

const STAGE_COLORS: Record<string, string> = {
  ai_generation: 'var(--color-danger-strong)',
  result_upload: 'var(--color-warning)',
  thumbnail_generation: '#8957e5',
  unknown: 'var(--color-subtle)',
};

/** `aiModel` is the ImageModel identifier; the provider model it resolved to is
 *  only worth showing when the two differ (variants share a provider model). */
const providerModelWorthShowing = (log: FailedLog) =>
  !!log.providerModel && log.providerModel !== log.aiModel;

const requestParamRows = (params?: RequestParams): Array<[string, string]> => {
  if (!params) return [];
  const rows: Array<[string, string]> = [];
  if (params.aspectRatio) rows.push(['aspect ratio', params.aspectRatio]);
  if (params.imageSize) rows.push(['image size', params.imageSize]);
  if (typeof params.numberOfImages === 'number') rows.push(['images requested', String(params.numberOfImages)]);
  if (typeof params.inputImageCount === 'number') rows.push(['input images', String(params.inputImageCount)]);
  if (typeof params.isBlendMode === 'boolean') rows.push(['blend mode', params.isBlendMode ? 'yes' : 'no']);
  if (typeof params.allowFallbackProvider === 'boolean') {
    rows.push(['fallback allowed', params.allowFallbackProvider ? 'yes' : 'no']);
  }
  for (const extra of params.extraParams || []) {
    rows.push([`extra · ${extra.key}`, String(extra.value)]);
  }
  return rows;
};

const formatDuration = (ms?: number) =>
  typeof ms === 'number' ? `${(ms / 1000).toFixed(1)}s` : null;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export default function GenerationFailedLogsPage() {
  const [logs, setLogs] = useState<FailedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [triggerType, setTriggerType] = useState<TriggerType>('all');
  const [errorStage, setErrorStage] = useState<ErrorStage>('all');
  const [outcome, setOutcome] = useState<Outcome>('all');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchLogs = useCallback(async (
    page: number,
    limit: number,
    trigger: TriggerType,
    stage: ErrorStage,
    userId: string,
    rows: Outcome,
  ) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        triggerType: trigger,
        errorStage: stage,
        ...OUTCOME_QUERY[rows],
        ...(userId && { userId }),
      });
      const response = await apiRequest<{ success: boolean; data: FailedLog[]; pagination: PaginationInfo }>(
        `/api/admin/image-generation-failed-logs?${params}`,
      );
      setLogs(response.data || []);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(currentPage, pageSize, triggerType, errorStage, userIdFilter, outcome);
  }, [currentPage, pageSize, triggerType, errorStage, userIdFilter, outcome, fetchLogs]);

  const applyFilters = () => {
    setCurrentPage(1);
    setUserIdFilter(userIdInput.trim());
  };

  const clearFilters = () => {
    setTriggerType('all');
    setErrorStage('all');
    setOutcome('all');
    setUserIdInput('');
    setUserIdFilter('');
    setCurrentPage(1);
  };

  const badge = (value: string, colors: Record<string, string>) => (
    <span style={{
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: colors[value] || 'var(--color-subtle)',
      color: 'white',
      whiteSpace: 'nowrap',
    }}>
      {value.replace(/_/g, ' ')}
    </span>
  );

  const recoveredBadge = (log: FailedLog) => (
    <span style={{
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: log.recovered ? 'var(--color-success-strong)' : 'var(--color-danger-strong)',
      color: 'white',
      whiteSpace: 'nowrap',
    }}>
      {log.recovered ? 'recovered' : 'not recovered'}
    </span>
  );

  const requestParamsBlock = (log: FailedLog) => {
    const rows = requestParamRows(log.requestParams);
    if (rows.length === 0) return null;
    return (
      <div style={{ marginBottom: '10px' }}>
        <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '4px' }}>REQUEST PARAMETERS</div>
        <div style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', padding: '10px 14px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '12px' }}>
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <span style={{ color: 'var(--color-muted)', whiteSpace: 'nowrap' }}>{label}</span>
              <code style={{ color: 'var(--color-text)', wordBreak: 'break-word' }}>{value}</code>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /** Attempt rows carry the provider that failed and where it sat in the retry
   *  chain; generation rows are the whole-generation failure and have neither. */
  const attemptCell = (log: FailedLog) => {
    if (log.scope !== 'attempt') {
      return <span style={{ color: 'var(--color-subtle)', fontSize: '13px' }}>whole generation</span>;
    }
    const duration = formatDuration(log.durationMs);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ color: 'var(--color-text)', fontSize: '13px', fontWeight: 600 }}>
            attempt {log.attemptNumber ?? '?'}
          </span>
          {recoveredBadge(log)}
        </div>
        <span style={{ color: 'var(--color-subtle)', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          {log.provider || '—'}{duration ? ` · ${duration}` : ''}
        </span>
      </div>
    );
  };

  return (
    <div className="page">
      <div style={{ marginBottom: '20px' }}>
        <Link to="/generation-usage" style={{ color: '#79c0ff', float: 'right' }}>Generation usage →</Link>
        <h1 style={{ color: 'var(--color-text)', margin: 0, fontSize: '24px' }}>Generation Failed Logs</h1>
        <p style={{ color: 'var(--color-muted)', marginTop: '4px', fontSize: '14px' }}>
          AI image generation failures — last 90 days. One row per failed generation, plus one
          per failed provider attempt: an attempt marked <em>recovered</em> was rescued by a later
          attempt. This indicates AI recovery; view the full attempts and credit status in Generation Usage.
        </p>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'flex-end',
      }}>
        <div>
          <label style={{ color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Rows</label>
          <select
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value as Outcome); setCurrentPage(1); }}
            style={{ padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px' }}
          >
            <option value="all">All rows</option>
            <option value="generation">Failed generations</option>
            <option value="attempt">Failed attempts</option>
            <option value="recovered">Failed attempts — recovered</option>
            <option value="unrecovered">Failed attempts — not recovered</option>
          </select>
        </div>

        <div>
          <label style={{ color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Trigger</label>
          <select
            value={triggerType}
            onChange={(e) => { setTriggerType(e.target.value as TriggerType); setCurrentPage(1); }}
            style={{ padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px' }}
          >
            <option value="all">All triggers</option>
            <option value="rebuild">Rebuild</option>
            <option value="blend">Blend</option>
            <option value="extension_use">Lens use</option>
            <option value="extension_create">Lens create</option>
            <option value="upscale">Upscale</option>
            <option value="custom">Custom</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>

        <div>
          <label style={{ color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Error stage</label>
          <select
            value={errorStage}
            onChange={(e) => { setErrorStage(e.target.value as ErrorStage); setCurrentPage(1); }}
            style={{ padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px' }}
          >
            <option value="all">All stages</option>
            <option value="ai_generation">AI generation</option>
            <option value="result_upload">Result upload</option>
            <option value="thumbnail_generation">Thumbnail generation</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>

        <div>
          <label style={{ color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>User ID</label>
          <input
            type="text"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Filter by userId…"
            style={{ padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px', width: '200px' }}
          />
        </div>

        <button
          onClick={applyFilters}
          style={{ padding: '7px 16px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
        >
          Apply
        </button>

        <button
          onClick={clearFilters}
          style={{ padding: '7px 16px', backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
        >
          Clear
        </button>

        <div style={{ marginLeft: 'auto' }}>
          <label style={{ color: 'var(--color-muted)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Per page</label>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            style={{ padding: '7px 10px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '14px' }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Stats bar */}
      {pagination && !loading && (
        <p style={{ color: 'var(--color-muted)', fontSize: '13px', marginBottom: '12px' }}>
          Showing {logs.length} of {pagination.total} rows
        </p>
      )}

      {/* Error */}
      {error && (
        <div style={{ color: 'var(--color-danger)', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-danger-strong)', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Mobile card list */}
      {isMobile ? (
        loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>No failed logs found</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {logs.map((log) => {
              const expanded = expandedId === log._id;
              return (
                <div
                  key={log._id}
                  onClick={() => setExpandedId(expanded ? null : log._id)}
                  style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {badge(log.triggerType, TRIGGER_COLORS)}
                    {badge(log.errorStage, STAGE_COLORS)}
                    {log.scope === 'attempt' && recoveredBadge(log)}
                  </div>
                  {log.scope === 'attempt' && (
                    <div style={{ fontSize: '12px', color: 'var(--color-muted)' }}>
                      attempt {log.attemptNumber ?? '?'} ·{' '}
                      <span style={{ fontFamily: 'monospace' }}>{log.provider || '—'}</span>
                      {formatDuration(log.durationMs) ? ` · ${formatDuration(log.durationMs)}` : ''}
                    </div>
                  )}
                  <div style={{ color: 'var(--color-danger)', fontSize: '13px', wordBreak: 'break-word' }}>
                    {log.errorCode && (
                      <span style={{ color: 'var(--color-warning)', fontFamily: 'monospace', marginRight: '6px' }}>{log.errorCode}</span>
                    )}
                    {expanded ? log.errorMessage : (log.errorMessage.length > 120 ? log.errorMessage.slice(0, 120) + '…' : log.errorMessage)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-muted)', display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    <span>{formatDate(log.createdAt)}</span>
                    {log.aiModel && <span>· {log.aiModel}</span>}
                  </div>
                  {log.userId && (
                    <div style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                      <span style={{ color: 'var(--color-subtle)' }}>user: </span>
                      <span style={{ color: 'var(--color-text)' }}>
                        {log.userName || <span style={{ color: 'var(--color-subtle)', fontStyle: 'italic' }}>Unknown user</span>}
                      </span>
                      <span style={{ color: 'var(--color-subtle)', fontFamily: 'monospace' }}> ({log.userId})</span>
                    </div>
                  )}
                  {expanded && (
                    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {log.generationId && (
                        <div><div style={{ color: 'var(--color-muted)', fontSize: '11px' }}>GENERATION ID</div><code style={{ color: '#79c0ff', fontSize: '12px', wordBreak: 'break-all' }}>{log.generationId}</code></div>
                      )}
                      {log.extensionId && (
                        <div><div style={{ color: 'var(--color-muted)', fontSize: '11px' }}>EXTENSION ID</div><code style={{ color: '#79c0ff', fontSize: '12px', wordBreak: 'break-all' }}>{log.extensionId}</code></div>
                      )}
                      {log.generationLogId && (
                        <div><div style={{ color: 'var(--color-muted)', fontSize: '11px' }}>GENERATION LOG ID</div><code style={{ color: '#79c0ff', fontSize: '12px', wordBreak: 'break-all' }}>{log.generationLogId}</code> <Link to={`/generation-usage?generationLogId=${encodeURIComponent(log.generationLogId)}`} style={{ color: '#79c0ff' }}>View attempts & credits →</Link></div>
                      )}
                      {providerModelWorthShowing(log) && (
                        <div><div style={{ color: 'var(--color-muted)', fontSize: '11px' }}>PROVIDER MODEL</div><code style={{ color: '#79c0ff', fontSize: '12px', wordBreak: 'break-all' }}>{log.providerModel}</code></div>
                      )}
                      {requestParamsBlock(log)}
                      {log.prompt && (
                        <div>
                          <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '4px' }}>PROMPT</div>
                          <div style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', padding: '8px 10px', color: 'var(--color-text)', fontSize: '13px', wordBreak: 'break-word' }}>{log.prompt}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
      /* Table */
      <div style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>No failed logs found</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-surface-hover)', borderBottom: '2px solid var(--color-border)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trigger</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Attempt</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Model</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: 'var(--color-muted)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log._id}>
                  <tr
                    key={log._id}
                    onClick={() => setExpandedId(expandedId === log._id ? null : log._id)}
                    style={{
                      borderBottom: expandedId === log._id ? 'none' : '1px solid var(--color-border)',
                      cursor: 'pointer',
                      backgroundColor: expandedId === log._id ? 'var(--color-surface-hover)' : 'transparent',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => { if (expandedId !== log._id) e.currentTarget.style.backgroundColor = 'var(--color-surface-secondary)'; }}
                    onMouseLeave={(e) => { if (expandedId !== log._id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <td style={{ padding: '12px 16px', color: 'var(--color-muted)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {formatDate(log.createdAt)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>{badge(log.triggerType, TRIGGER_COLORS)}</td>
                    <td style={{ padding: '12px 16px' }}>{badge(log.errorStage, STAGE_COLORS)}</td>
                    <td style={{ padding: '12px 16px' }}>{attemptCell(log)}</td>
                    <td style={{ padding: '12px 16px', maxWidth: '180px' }}>
                      {log.userId ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflow: 'hidden' }}>
                          <span style={{ color: 'var(--color-text)', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.userName || undefined}>
                            {log.userName || <span style={{ color: 'var(--color-subtle)', fontStyle: 'italic' }}>Unknown user</span>}
                          </span>
                          <span style={{ color: 'var(--color-subtle)', fontSize: '11px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.userId}>
                            {log.userId}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-subtle)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-muted)', fontSize: '13px' }}>
                      {log.aiModel || <span style={{ color: 'var(--color-subtle)' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-danger)', fontSize: '13px', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.errorCode && (
                        <span style={{ color: 'var(--color-warning)', fontFamily: 'monospace', marginRight: '6px' }}>{log.errorCode}</span>
                      )}
                      {log.errorMessage}
                    </td>
                  </tr>

                  {expandedId === log._id && (
                    <tr key={`${log._id}-detail`} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                      <td colSpan={7} style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                          {log.generationId && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>GENERATION ID</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{log.generationId}</code>
                            </div>
                          )}
                          {log.extensionId && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>EXTENSION ID</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{log.extensionId}</code>
                            </div>
                          )}
                          {log.generationLogId && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>GENERATION LOG ID</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{log.generationLogId}</code> <Link to={`/generation-usage?generationLogId=${encodeURIComponent(log.generationLogId)}`} style={{ color: '#79c0ff' }}>View attempts & credits →</Link>
                            </div>
                          )}
                          {log.provider && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>PROVIDER</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{log.provider}</code>
                            </div>
                          )}
                          {(log.errorCode || log.errorCategory) && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>ERROR CODE</div>
                              <code style={{ color: 'var(--color-warning)', fontSize: '12px' }}>
                                {[log.errorCode, log.errorCategory].filter(Boolean).join(' · ')}
                              </code>
                            </div>
                          )}
                          {formatDuration(log.durationMs) && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>ATTEMPT DURATION</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{formatDuration(log.durationMs)}</code>
                            </div>
                          )}
                          {providerModelWorthShowing(log) && (
                            <div>
                              <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '2px' }}>PROVIDER MODEL</div>
                              <code style={{ color: '#79c0ff', fontSize: '12px' }}>{log.providerModel}</code>
                            </div>
                          )}
                        </div>
                        {requestParamsBlock(log)}
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '4px' }}>FULL ERROR</div>
                          <div style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-danger-strong)', borderRadius: '6px', padding: '10px 14px', color: 'var(--color-danger)', fontSize: '13px', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                            {log.errorMessage}
                          </div>
                        </div>
                        {log.prompt && (
                          <div>
                            <div style={{ color: 'var(--color-muted)', fontSize: '11px', marginBottom: '4px' }}>PROMPT</div>
                            <div style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '6px', padding: '10px 14px', color: 'var(--color-text)', fontSize: '13px', wordBreak: 'break-word' }}>
                              {log.prompt}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div style={{ marginTop: '16px' }}>
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.pages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}
